import React, { useState, useEffect, useRef } from 'react';
import { collection, getDocs, updateDoc, doc, query, where  } from 'firebase/firestore';
import { db } from '../config/firebase';
import { Clock, GripVertical, X, Tv, Calendar, Plug } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import TVPowerTab from './TVPowerTab';  
import OperationalTab from './OperationalTab';

// Time Picker Popover Component
function TimePickerPopover({ time, onChange, onClose }) {
  const popoverRef = useRef(null);
  const hours = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];
  const minutes = Array.from({ length: 60 }, (_, i) => i);
  
  // Parse time string "HH:MM" to object
  const parseTime = (timeStr) => {
    if (!timeStr) return { hour: 6, minute: 0, period: 'AM' };
    const [h, m] = timeStr.split(':').map(Number);
    let hour = h;
    let period = 'AM';
    
    if (h === 0) {
      hour = 12;
      period = 'AM';
    } else if (h === 12) {
      hour = 12;
      period = 'PM';
    } else if (h > 12) {
      hour = h - 12;
      period = 'PM';
    } else {
      hour = h;
      period = 'AM';
    }
    
    return { hour, minute: m, period };
  };
  
  // Convert object back to "HH:MM" string
  const formatTime = (obj) => {
    let hour24 = obj.hour;
    if (obj.period === 'AM') {
      if (obj.hour === 12) hour24 = 0;
    } else {
      if (obj.hour !== 12) hour24 = obj.hour + 12;
    }
    return `${hour24.toString().padStart(2, '0')}:${obj.minute.toString().padStart(2, '0')}`;
  };
  
  const [tempTime, setTempTime] = useState(parseTime(time));
  
  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);
  
  const handleApply = () => {
    onChange(formatTime(tempTime));
    onClose();
  };
  
  return (
    <div 
      ref={popoverRef}
      style={{
        position: 'absolute',
        top: '100%',
        left: 0,
        marginTop: '8px',
        background: 'white',
        borderRadius: '12px',
        boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
        padding: '16px',
        zIndex: 1000,
        minWidth: '260px'
      }}
    >
      <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', alignItems: 'center' }}>
        {/* Hour */}
        <select
          value={tempTime.hour}
          onChange={(e) => setTempTime({ ...tempTime, hour: parseInt(e.target.value) })}
          style={{
            flex: 1,
            padding: '10px',
            border: '1px solid #ddd',
            borderRadius: '6px',
            fontSize: '16px',
            background: 'white',
            cursor: 'pointer'
          }}
        >
          {hours.map(h => (
            <option key={h} value={h}>{h.toString().padStart(2, '0')}</option>
          ))}
        </select>
        
        <span style={{ fontSize: '18px', color: '#666' }}>:</span>
        
        {/* Minute */}
        <select
          value={tempTime.minute}
          onChange={(e) => setTempTime({ ...tempTime, minute: parseInt(e.target.value) })}
          style={{
            flex: 1,
            padding: '10px',
            border: '1px solid #ddd',
            borderRadius: '6px',
            fontSize: '16px',
            background: 'white',
            cursor: 'pointer'
          }}
        >
          {minutes.map(m => (
            <option key={m} value={m}>{m.toString().padStart(2, '0')}</option>
          ))}
        </select>
        
        {/* AM/PM */}
        <select
          value={tempTime.period}
          onChange={(e) => setTempTime({ ...tempTime, period: e.target.value })}
          style={{
            padding: '10px',
            border: '1px solid #ddd',
            borderRadius: '6px',
            fontSize: '16px',
            background: 'white',
            cursor: 'pointer'
          }}
        >
          <option value="AM">AM</option>
          <option value="PM">PM</option>
        </select>
      </div>
      
      {/* Actions */}
      <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
        <button
          onClick={onClose}
          style={{
            padding: '8px 16px',
            border: '1px solid #ddd',
            borderRadius: '6px',
            background: 'white',
            cursor: 'pointer',
            fontSize: '14px'
          }}
        >
          Cancel
        </button>
        <button
          onClick={handleApply}
          style={{
            padding: '8px 16px',
            border: 'none',
            borderRadius: '6px',
            background: '#E63946',
            color: 'white',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: 500
          }}
        >
          Apply
        </button>
      </div>
    </div>
  );
}

// Clickable Time Field Component
function TimeField({ label, value, onChange }) {
  const [showPicker, setShowPicker] = useState(false);
  
  // Format 24h time to display format
  const formatDisplay = (timeStr) => {
    if (!timeStr) return '12:00 AM';
    const [h, m] = timeStr.split(':').map(Number);
    let hour = h;
    let period = 'AM';
    
    if (h === 0) {
      hour = 12;
      period = 'AM';
    } else if (h === 12) {
      hour = 12;
      period = 'PM';
    } else if (h > 12) {
      hour = h - 12;
      period = 'PM';
    } else {
      hour = h;
      period = 'AM';
    }
    
    return `${hour.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')} ${period}`;
  };
  
  return (
    <div className="form-group" style={{ flex: 1, position: 'relative' }}>
      <label className="form-label" style={{ fontSize: '11px', fontWeight: 500, letterSpacing: '0.3px' }}>
  <Clock size={14} style={{ marginRight: '6px' }} />
  {label}
</label>
      <div
        onClick={() => setShowPicker(true)}
        style={{
          padding: '12px 16px',
          border: '1px solid #ddd',
          borderRadius: '8px',
          cursor: 'pointer',
          fontSize: '15px',
          fontWeight: 500,
          background: showPicker ? '#f0f7ff' : 'white',
          borderColor: showPicker ? '#2196F3' : '#ddd',
          transition: 'all 0.15s ease'
        }}
      >
        {formatDisplay(value)}
      </div>
      
      {showPicker && (
        <TimePickerPopover
          time={value}
          onChange={onChange}
          onClose={() => setShowPicker(false)}
        />
      )}
    </div>
  );
}

// Swap Time Field Component (for Tomorrow Content section)
// Simple dropdown with 8 time slots that match the Cloud Function schedule (every 3 hours)
function SwapTimeField({ swapTime, setSwapTime }) {
  // 8 time slots - every 3 hours
  const timeSlots = [
    { value: '00:00', label: '12:00 AM' },
    { value: '03:00', label: '3:00 AM' },
    { value: '06:00', label: '6:00 AM' },
    { value: '09:00', label: '9:00 AM' },
    { value: '12:00', label: '12:00 PM' },
    { value: '15:00', label: '3:00 PM' },
    { value: '18:00', label: '6:00 PM' },
    { value: '21:00', label: '9:00 PM' },
  ];
  
  // Convert swapTime state to 24hr string for the select value
  const getCurrentValue = () => {
    let hour = parseInt(swapTime.hour) || 12;
    if (swapTime.period === 'AM') {
      if (hour === 12) hour = 0;
    } else {
      if (hour !== 12) hour = hour + 12;
    }
    return `${hour.toString().padStart(2, '0')}:00`;
  };
  
  const handleChange = (e) => {
    const value = e.target.value;
    const [hour24] = value.split(':').map(Number);
    let hour12 = hour24;
    let period = 'AM';
    
    if (hour24 === 0) {
      hour12 = 12;
      period = 'AM';
    } else if (hour24 === 12) {
      hour12 = 12;
      period = 'PM';
    } else if (hour24 > 12) {
      hour12 = hour24 - 12;
      period = 'PM';
    } else {
      hour12 = hour24;
      period = 'AM';
    }
    
    setSwapTime({
      hour: hour12.toString().padStart(2, '0'),
      minute: '00',
      period: period
    });
  };
  
  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      flexWrap: 'wrap'
    }}>
      <label style={{
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        fontSize: '14px',
        color: '#059669'
      }}>
        <Clock size={16} />
        Swap Time
      </label>
      
      <select
        value={getCurrentValue()}
        onChange={handleChange}
        style={{
          padding: '10px 16px',
          border: '1px solid #bbf7d0',
          borderRadius: '8px',
          fontSize: '16px',
          fontWeight: 500,
          background: 'white',
          color: '#333',
          cursor: 'pointer',
          minWidth: '130px'
        }}
      >
        {timeSlots.map((slot) => (
          <option key={slot.value} value={slot.value}>
            {slot.label}
          </option>
        ))}
      </select>
    </div>
  );
}



// ═══════════════════════════════════════════════════════════════
//  MAIN SCHEDULES COMPONENT
// ═══════════════════════════════════════════════════════════════
function Schedules() {
  const [playlists, setPlaylists] = useState([]);
  const [assets, setAssets] = useState([]);
  const [selectedPlaylist, setSelectedPlaylist] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editSchedule, setEditSchedule] = useState({ startTime: '', endTime: '' });
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [showMobileSheet, setShowMobileSheet] = useState(false);
  const { currentUser, isAdmin } = useAuth();
  
  // Swap time state
  const [swapTime, setSwapTime] = useState({ hour: '12', minute: '00', period: 'AM' });

  // ─── NEW: Tab state ───
  const [activeTab, setActiveTab] = useState('content');

  // Initial data load
  useEffect(() => {
    const loadInitialData = async () => {
      try {
       const playlistsQuery = isAdmin 
  ? collection(db, 'playlists')
  : query(collection(db, 'playlists'), where('userId', '==', currentUser?.uid));

const [playlistsSnapshot, assetsSnapshot] = await Promise.all([
  getDocs(playlistsQuery),
  getDocs(collection(db, 'assets'))
]);

        const playlistsData = playlistsSnapshot.docs.map(d => ({
          id: d.id,
          ...d.data()
        })).filter(playlist => {
  if (isAdmin) return !playlist.userId || playlist.userId === currentUser?.uid;
  return playlist.userId === currentUser?.uid;
});

        const assetsData = assetsSnapshot.docs.map(d => ({
          id: d.id,
          ...d.data()
        }));

        // Sort by order field
        playlistsData.sort((a, b) => {
          if (a.order !== undefined && b.order !== undefined) {
            return a.order - b.order;
          }
          return (a.name || '').localeCompare(b.name || '');
        });

        setPlaylists(playlistsData);
        setAssets(assetsData);

        // Auto-select first playlist
        if (playlistsData.length > 0) {
          const firstPlaylist = playlistsData[0];
          setSelectedPlaylist(firstPlaylist);
          setEditSchedule({
            startTime: firstPlaylist.startTime || '06:00',
            endTime: firstPlaylist.endTime || '19:30'
          });
          if (firstPlaylist.swapTime) {
            setSwapTime(parseSwapTime(firstPlaylist.swapTime));
          }
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    
    loadInitialData();
  }, [isAdmin, currentUser?.uid]);

  // Refresh function for after updates
  const fetchData = async () => {
    try {
      const playlistsQuery = isAdmin 
  ? collection(db, 'playlists')
  : query(collection(db, 'playlists'), where('userId', '==', currentUser?.uid));

const [playlistsSnapshot, assetsSnapshot] = await Promise.all([
  getDocs(playlistsQuery),
  getDocs(collection(db, 'assets'))
]);

      const playlistsData = playlistsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })).filter(playlist => {
  if (isAdmin) return !playlist.userId || playlist.userId === currentUser?.uid;
  return playlist.userId === currentUser?.uid;
});

      const assetsData = assetsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      playlistsData.sort((a, b) => {
        if (a.order !== undefined && b.order !== undefined) {
          return a.order - b.order;
        }
        return (a.name || '').localeCompare(b.name || '');
      });

      setPlaylists(playlistsData);
      setAssets(assetsData);

      if (selectedPlaylist) {
        const updated = playlistsData.find(p => p.id === selectedPlaylist.id);
        if (updated) {
          setSelectedPlaylist(updated);
          if (updated.swapTime) {
            const parsed = parseSwapTime(updated.swapTime);
            setSwapTime(parsed);
          }
        }
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const fetchPlaylists = fetchData;

  const parseSwapTime = (timeStr) => {
    if (!timeStr) return { hour: '12', minute: '00', period: 'AM' };
    
    const [hours, minutes] = timeStr.split(':');
    let hour = parseInt(hours);
    const period = hour >= 12 ? 'PM' : 'AM';
    
    if (hour === 0) hour = 12;
    else if (hour > 12) hour = hour - 12;
    
    return {
      hour: hour.toString().padStart(2, '0'),
      minute: minutes || '00',
      period
    };
  };

  const formatSwapTimeDisplay = (playlist) => {
    if (!playlist.swapTime) return '12:00 AM';
    const parsed = parseSwapTime(playlist.swapTime);
    return `${parseInt(parsed.hour)}:${parsed.minute} ${parsed.period}`;
  };

  const formatSwapTimeFor24Hour = (swapTimeObj) => {
    let hour = parseInt(swapTimeObj.hour);
    
    if (swapTimeObj.period === 'AM') {
      if (hour === 12) hour = 0;
    } else {
      if (hour !== 12) hour = hour + 12;
    }
    
    return `${hour.toString().padStart(2, '0')}:${swapTimeObj.minute}`;
  };

  const getAssetById = (assetId) => {
    return assets.find(a => a.id === assetId);
  };

  const formatDuration = (ms) => {
    if (!ms) return '0.0 sec';
    const seconds = (ms / 1000).toFixed(1);
    return `${seconds} sec`;
  };

  const hasPendingContent = (playlist) => {
    return playlist.pendingAssets && playlist.pendingAssets.length > 0;
  };

  const getFirstPendingAsset = (playlist) => {
    if (!hasPendingContent(playlist)) return null;
    return getAssetById(playlist.pendingAssets[0]);
  };

  const handleSelectPlaylist = (playlist) => {
    setSelectedPlaylist(playlist);
    setEditSchedule({
      startTime: playlist.startTime || '06:00',
      endTime: playlist.endTime || '19:30'
    });
    
    if (playlist.swapTime) {
      setSwapTime(parseSwapTime(playlist.swapTime));
    } else {
      setSwapTime({ hour: '12', minute: '00', period: 'AM' });
    }
    
    setShowMobileSheet(true);
  };

  const handleCloseSheet = () => {
    setShowMobileSheet(false);
  };

  const handleSaveSchedule = async () => {
    if (!selectedPlaylist) return;

    try {
      await updateDoc(doc(db, 'playlists', selectedPlaylist.id), {
        startTime: editSchedule.startTime,
        endTime: editSchedule.endTime
      });

      setSelectedPlaylist({
        ...selectedPlaylist,
        startTime: editSchedule.startTime,
        endTime: editSchedule.endTime
      });

      fetchPlaylists();
      
      setShowMobileSheet(false);
      
      alert('Schedule saved!');
    } catch (error) {
      console.error('Error saving schedule:', error);
    }
  };

  const handleSaveSwapTime = async () => {
  if (!selectedPlaylist) return;

  try {
    const swapTime24 = formatSwapTimeFor24Hour(swapTime);
    
    // Calculate the next occurrence of this time as a full timestamp
    const [swapHour, swapMinute] = swapTime24.split(':').map(Number);
    const now = new Date();
    const swapDate = new Date();
    
    swapDate.setHours(swapHour, swapMinute, 0, 0);
    
    // If this time has already passed today, set it for tomorrow
    if (swapDate <= now) {
      swapDate.setDate(swapDate.getDate() + 1);
    }
    
    const swapAfter = swapDate.getTime();
    
    await updateDoc(doc(db, 'playlists', selectedPlaylist.id), {
      swapTime: swapTime24,
      swapAfter: swapAfter
    });

    setSelectedPlaylist({
      ...selectedPlaylist,
      swapTime: swapTime24,
      swapAfter: swapAfter
    });

    fetchPlaylists();
    
    alert('Swap time updated!');
  } catch (error) {
    console.error('Error saving swap time:', error);
  }
};
  // Drag and Drop handlers
  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (index !== draggedIndex) {
      setDragOverIndex(index);
    }
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDrop = async (e, dropIndex) => {
    e.preventDefault();
    
    if (draggedIndex === null || draggedIndex === dropIndex) {
      setDraggedIndex(null);
      setDragOverIndex(null);
      return;
    }

    const newPlaylists = [...playlists];
    const [draggedItem] = newPlaylists.splice(draggedIndex, 1);
    newPlaylists.splice(dropIndex, 0, draggedItem);

    setPlaylists(newPlaylists);
    setDraggedIndex(null);
    setDragOverIndex(null);

    try {
      const updatePromises = newPlaylists.map((playlist, index) => 
        updateDoc(doc(db, 'playlists', playlist.id), { order: index })
      );
      await Promise.all(updatePromises);
    } catch (error) {
      console.error('Error saving playlist order:', error);
      fetchPlaylists();
    }
  };

  // Schedule Editor Component (shared between desktop and mobile)
  const ScheduleEditor = ({ isMobile = false }) => {
    const pendingAsset = getFirstPendingAsset(selectedPlaylist);
    const pendingCount = selectedPlaylist.pendingAssets?.length || 0;

    return (
      <div className={isMobile ? "" : "card"}>
        <h2 style={{ marginBottom: '24px', fontSize: isMobile ? '18px' : '18px', fontWeight: 600, letterSpacing: '-0.2px' }}>
          {selectedPlaylist.name} Schedule
        </h2>

        <div style={{ display: 'flex', gap: '16px', marginBottom: '24px' }}>
          <TimeField
            label="Start Time"
            value={editSchedule.startTime}
            onChange={(newTime) => setEditSchedule({...editSchedule, startTime: newTime})}
          />
          <TimeField
            label="End Time"
            value={editSchedule.endTime}
            onChange={(newTime) => setEditSchedule({...editSchedule, endTime: newTime})}
          />
        </div>

        <button className="btn btn-primary" style={{ padding: '9px 18px', fontSize: '13px' }} onClick={handleSaveSchedule}>
  Save Schedule
</button>

        {/* Visual Schedule Display */}
        <div style={{ marginTop: '24px' }}>
          <h3 style={{ marginBottom: '12px', fontSize: '16px' }}>Schedule Preview</h3>
          <div style={{ 
            background: '#f5f5f5', 
            borderRadius: '8px', 
            padding: '16px',
            position: 'relative',
            height: '80px'
          }}>
            <div style={{
              position: 'absolute',
              left: `${(parseInt(editSchedule.startTime.split(':')[0]) / 24) * 100}%`,
              right: `${100 - (parseInt(editSchedule.endTime.split(':')[0]) / 24) * 100}%`,
              top: '16px',
              bottom: '30px',
              backgroundColor: '#d32f2f',
              borderRadius: '4px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              fontSize: '12px',
              minWidth: '80px'
            }}>
              {editSchedule.startTime} - {editSchedule.endTime}
            </div>
            <div style={{ 
              position: 'absolute', 
              bottom: '4px', 
              left: '0', 
              right: '0', 
              display: 'flex', 
              justifyContent: 'space-between',
              fontSize: '11px',
              color: '#999',
              padding: '0 4px'
            }}>
              <span>12am</span>
              <span>6am</span>
              <span>12pm</span>
              <span>6pm</span>
              <span>12am</span>
            </div>
          </div>
        </div>

        {/* Tomorrow Content Swap Section */}
        <div style={{ 
          borderTop: '1px solid #eee', 
          marginTop: '32px', 
          paddingTop: '24px' 
        }}>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '12px', 
            marginBottom: '16px' 
          }}>
            <h3 style={{ fontSize: '16px', margin: 0 }}>Tomorrow Content Swap</h3>
            {hasPendingContent(selectedPlaylist) && (
              <span style={{
                backgroundColor: '#d1fae5',
                color: '#059669',
                fontSize: '12px',
                fontWeight: '500',
                padding: '4px 10px',
                borderRadius: '12px'
              }}>
                Content Queued
              </span>
            )}
          </div>

          {hasPendingContent(selectedPlaylist) ? (
            <div style={{
              backgroundColor: '#f0fdf4',
              border: '1px solid #bbf7d0',
              borderRadius: '8px',
              padding: '16px'
            }}>
              {/* Pending Asset Info */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: '16px'
              }}>
                <div>
                  <p style={{ margin: '0 0 4px', fontSize: '13px', color: '#666' }}>
                    Pending asset{pendingCount > 1 ? 's' : ''}:
                  </p>
                  <p style={{ margin: 0, fontSize: '15px', fontWeight: '500', color: '#333' }}>
                    {pendingAsset ? pendingAsset.name : 'Unknown asset'}
                    {pendingCount > 1 && (
                      <span style={{ color: '#666', fontWeight: '400' }}>
                        {' '}(+{pendingCount - 1} more)
                      </span>
                    )}
                  </p>
                </div>
                {pendingAsset && (
                  <span style={{ fontSize: '13px', color: '#666' }}>
                    {formatDuration(pendingAsset.durationMs)}
                  </span>
                )}
              </div>

              {/* Swap Time Picker */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '16px',
                flexWrap: 'wrap'
              }}>
                <SwapTimeField 
                  swapTime={swapTime}
                  setSwapTime={setSwapTime}
                />
                <button
                  onClick={handleSaveSwapTime}
                  style={{
                    backgroundColor: '#10b981',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    padding: '8px 16px',
                    fontSize: '13px',
                    fontWeight: '500',
                    cursor: 'pointer'
                  }}
                >
                  Update Swap Time
                </button>
              </div>
            </div>
          ) : (
            <div style={{
              backgroundColor: '#f9f9f9',
              border: '1px dashed #ddd',
              borderRadius: '8px',
              padding: '24px',
              textAlign: 'center'
            }}>
              <p style={{ margin: 0, color: '#999', fontSize: '14px' }}>
                No content queued for tomorrow.
              </p>
            </div>
          )}
        </div>
      </div>
    );
  };

  if (loading) {
    return <div className="loading">Loading schedules...</div>;
  }

  return (
    <div>
      {/* ─── Page Header with Tabs ─── */}
      <div className="page-header desktop-only" style={{ display: 'flex', alignItems: 'center', gap: '24px' }}>
        <h1 className="page-title" style={{ marginBottom: 0, fontSize: '20px', fontWeight: 500 }}>Schedules</h1>
        <div style={{ display: 'flex', gap: '4px', background: 'white', borderRadius: '8px', padding: '4px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
          <button
            onClick={() => setActiveTab('content')}
            style={{
              display: 'flex', alignItems: 'center', gap: '6px',
              padding: '8px 16px', borderRadius: '6px', fontSize: '14px', fontWeight: 500,
              border: 'none', cursor: 'pointer', transition: 'all 0.15s',
              background: activeTab === 'content' ? '#E63946' : 'transparent',
              color: activeTab === 'content' ? 'white' : '#666'
            }}
          >
            <Calendar size={14} /> Content
          </button>
          <button
            onClick={() => setActiveTab('tvpower')}
            style={{
              display: 'flex', alignItems: 'center', gap: '6px',
              padding: '8px 16px', borderRadius: '6px', fontSize: '14px', fontWeight: 500,
              border: 'none', cursor: 'pointer', transition: 'all 0.15s',
              background: activeTab === 'tvpower' ? '#E63946' : 'transparent',
              color: activeTab === 'tvpower' ? 'white' : '#666'
            }}
          >
            <Plug size={14} /> Smart Power
          </button>
                    <button
            onClick={() => setActiveTab('operational')}
            style={{
              display: 'flex', alignItems: 'center', gap: '6px',
              padding: '8px 16px', borderRadius: '6px', fontSize: '14px', fontWeight: 500,
              border: 'none', cursor: 'pointer', transition: 'all 0.15s',
              background: activeTab === 'operational' ? '#E63946' : 'transparent',
              color: activeTab === 'operational' ? 'white' : '#666'
            }}
          >
            <Tv size={14} /> Operational
          </button>

        </div>
      </div>

      {/* ─── Mobile Tab Bar ─── */}
      <div className="mobile-only" style={{ marginBottom: '16px' }}>
        <div style={{
          display: 'flex', gap: '4px', background: 'white', borderRadius: '8px',
          padding: '4px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
        }}>
          <button
            onClick={() => setActiveTab('content')}
            style={{
              flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px',
              padding: '8px 8px', borderRadius: '6px', fontSize: '12px', fontWeight: 500,
              border: 'none', cursor: 'pointer', transition: 'all 0.15s',
              background: activeTab === 'content' ? '#E63946' : 'transparent',
              color: activeTab === 'content' ? 'white' : '#666',
            }}
          >
            <Calendar size={12} /> Content
          </button>
          <button
            onClick={() => setActiveTab('tvpower')}
            style={{
              flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px',
              padding: '8px 8px', borderRadius: '6px', fontSize: '12px', fontWeight: 500,
              border: 'none', cursor: 'pointer', transition: 'all 0.15s',
              background: activeTab === 'tvpower' ? '#E63946' : 'transparent',
              color: activeTab === 'tvpower' ? 'white' : '#666',
            }}
          >
            <Plug size={12} /> Smart Power
          </button>
          <button
            onClick={() => setActiveTab('operational')}
            style={{
              flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px',
              padding: '8px 8px', borderRadius: '6px', fontSize: '12px', fontWeight: 500,
              border: 'none', cursor: 'pointer', transition: 'all 0.15s',
              background: activeTab === 'operational' ? '#E63946' : 'transparent',
              color: activeTab === 'operational' ? 'white' : '#666',
            }}
          >
            <Tv size={12} /> Operational
          </button>
        </div>
      </div>

      {/* ─── CONTENT TAB (existing page, completely untouched) ─── */}
      {activeTab === 'content' && (
        <>
          <div className="sidebar-layout desktop-only">
            {/* Sidebar - Playlist List */}
            <div className="sidebar">
              <div className="card">
                <h3 style={{ marginBottom: '16px', fontSize: '14px', fontWeight: 500 }}>Playlists</h3>
                <div>
                  {playlists.map((playlist, index) => (
                    <div
                      key={playlist.id}
                      draggable
                      onDragStart={(e) => handleDragStart(e, index)}
                      onDragEnd={handleDragEnd}
                      onDragOver={(e) => handleDragOver(e, index)}
                      onDragLeave={handleDragLeave}
                      onDrop={(e) => handleDrop(e, index)}
                      className={`playlist-item ${selectedPlaylist?.id === playlist.id ? 'active' : ''}`}
                      onClick={() => handleSelectPlaylist(playlist)}
                      style={{
                        cursor: 'grab',
                        borderTop: dragOverIndex === index && draggedIndex > index ? '2px solid #d32f2f' : 'none',
                        borderBottom: dragOverIndex === index && draggedIndex < index ? '2px solid #d32f2f' : 'none',
                        opacity: draggedIndex === index ? 0.5 : 1,
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}
                    >
                      <GripVertical size={14} color="#999" style={{ flexShrink: 0 }} />
                      <div
                        className="playlist-dot"
                        style={hasPendingContent(playlist) ? { backgroundColor: '#10b981' } : undefined}
                        title={hasPendingContent(playlist) ? `Swaps at ${formatSwapTimeDisplay(playlist)}` : undefined}
                      ></div>
                      <span style={{ flex: 1 }}>{playlist.name}</span>
                      {playlist.playlistContentType && (
                        <span
                          title={playlist.playlistContentType === 'exercise' ? 'Exercise' : 'Interval'}
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '19px',
                            height: '19px',
                            borderRadius: '50%',
                            border: '1px solid #ccc',
                            fontSize: '11px',
                            fontWeight: 500,
                            color: '#666',
                            flexShrink: 0
                          }}
                        >
                          {playlist.playlistContentType === 'exercise' ? 'E' : 'I'}
                        </span>
                      )}
                    </div>
                  ))}
                </div>

                {playlists.length === 0 && (
                  <div style={{ textAlign: 'center', padding: '20px', color: '#999' }}>
                    No playlists yet. Create playlists first.
                  </div>
                )}
              </div>
            </div>

            {/* Main Content - Schedule Editor (Desktop) */}
            <div className="content-area">
              {selectedPlaylist ? (
                <ScheduleEditor />
              ) : (
                <div className="card" style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
                  Select a playlist to edit its schedule
                </div>
              )}
            </div>
          </div>

          {/* Mobile Playlist List */}
          <div className="mobile-only">
            <div className="mobile-list">
              {playlists.map((playlist) => (
                <div 
                  key={playlist.id} 
                  className="mobile-list-item"
                  onClick={() => handleSelectPlaylist(playlist)}
                >
                  <div
                    className="playlist-dot"
                    style={{ width: '12px', height: '12px', flexShrink: 0, ...(hasPendingContent(playlist) ? { backgroundColor: '#10b981' } : {}) }}
                    title={hasPendingContent(playlist) ? `Swaps at ${formatSwapTimeDisplay(playlist)}` : undefined}
                  ></div>
                  
                  <div className="mobile-list-content">
                    <div className="mobile-list-title">{playlist.name}</div>
                    <div className="mobile-list-subtitle">Schedules</div>
                  </div>

                  {playlist.playlistContentType && (
                    <span
                      title={playlist.playlistContentType === 'exercise' ? 'Exercise' : 'Interval'}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '20px',
                        height: '20px',
                        borderRadius: '50%',
                        border: '1px solid #ccc',
                        fontSize: '11px',
                        fontWeight: 500,
                        color: '#666',
                        flexShrink: 0,
                        marginRight: '8px'
                      }}
                    >
                      {playlist.playlistContentType === 'exercise' ? 'E' : 'I'}
                    </span>
                  )}
                </div>
              ))}

              {playlists.length === 0 && (
                <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
                  No playlists yet. Create playlists first.
                </div>
              )}
            </div>
          </div>

          {/* Mobile Bottom Sheet */}
          {showMobileSheet && selectedPlaylist && (
            <div className="mobile-only">
              <div 
                className="bottom-sheet-overlay"
                onClick={handleCloseSheet}
              />
              <div className="bottom-sheet">
                <div className="bottom-sheet-header">
                  <h3>Edit Schedule</h3>
                  <button 
                    className="bottom-sheet-close"
                    onClick={handleCloseSheet}
                  >
                    <X size={24} />
                  </button>
                </div>
                <div className="bottom-sheet-content">
                  <ScheduleEditor isMobile={true} />
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* ─── TV POWER TAB ─── */}
      {activeTab === 'tvpower' && (
        <TVPowerTab />
      )}
      
            {/* ─── OPERATIONAL TAB ─── */}
      {activeTab === 'operational' && (
        <OperationalTab />
      )}

    </div>
  );
}

export default Schedules;